"use client";

import { useMemo } from "react";
import { isComparisonBook, resolveComparisonColumns } from "@/components/optimizer/comparison-book-order";
import type { OptimizerComparisonOrderRuntime } from "@/components/optimizer/comparison-order-dialog";
import type { ColumnPreference } from "@/components/fantasy-props/model";
import type { PlatformType } from "@/components/optimizer/utils-v2";
import { useSportsbookPreferences } from "@/contexts/sportsbook-preferences-context";
import { getOptimizerComparisonOrderState } from "@/lib/sportsbook-preferences";

const NONE_HIDDEN: readonly never[] = [];

export function useComparisonOrder(fallback?: readonly PlatformType[]): { readonly order: PlatformType[]; readonly columns: ColumnPreference; readonly runtime: OptimizerComparisonOrderRuntime } {
  const { state, actions, meta } = useSportsbookPreferences();
  const saved = useMemo(() => getOptimizerComparisonOrderState(state), [state.comparisonOrder]);
  const columns = useMemo<ColumnPreference>(
    () => saved.kind === "supported"
      ? { order: resolveComparisonColumns(saved.value.bookIds), hidden: saved.value.hiddenIds ?? NONE_HIDDEN }
      : { order: resolveComparisonColumns(fallback), hidden: NONE_HIDDEN },
    [saved, fallback],
  );
  const order = useMemo(() => columns.order.filter(isComparisonBook), [columns]);
  const runtime = useMemo<OptimizerComparisonOrderRuntime>(() => {
    const unsupported = saved.kind === "unsupported";
    const unavailableReason = unsupported
      ? "This comparison order was saved by a newer version and cannot be changed here."
      : meta.status === "unavailable"
        ? "Comparison order is temporarily unavailable. Retry your account preferences before saving."
        : meta.status === "loading"
          ? "Comparison order is still loading."
          : null;
    return {
      order: columns.order,
      hidden: columns.hidden,
      isCustom: saved.kind === "supported",
      canWrite: meta.status === "ready" && !unsupported,
      isSaving: meta.isSaving,
      error: meta.error,
      unavailableReason,
      saveOrder: async (next, hidden) => (await actions.saveComparisonOrder(next, hidden)).success,
      resetOrder: async () => (await actions.resetComparisonOrder()).success,
    };
  }, [actions, saved, columns, meta]);
  return { order, columns, runtime };
}
